import { shade, roundRect, ellipse } from "./artPrimitives";
import { CELL_PX, NEUTRAL, Sprites } from "./sprites";
import { SPLIT_BOUNDARY, splitLid } from "./splitLid";
import { drawBoltSprite, drawElevatorSprite, drawIceSprite, drawLaserGateSprite, drawLaserKeySprite, drawMetalBoxSprite, drawSlidingDoorSprite, drawSuperMysterySprite, drawSuperTunnelSprite, drawToolBoxSprite, drawPipeSprite, } from "./itemSprites";
import { MarbleColor } from "../Models/LevelData";
import { GridDirection } from "../Models/CellData";
// MarbleColor -> hex, derived from the game's MarblePalette.ToColor floats (byte-rounded). Covers the
// full 13-colour set, sampled from the flat lid face of each <Colour>CloseBox.png in the art export.
export const MARBLE_COLOR_HEX = {
    [MarbleColor.Red01]: "#9A1B46",
    [MarbleColor.Green01]: "#2C6C1F",
    [MarbleColor.Blue01]: "#1FABBF",
    [MarbleColor.Blue02]: "#0A55A2",
    [MarbleColor.Yellow01]: "#EABF17",
    [MarbleColor.Purple]: "#5122B8",
    [MarbleColor.Pink]: "#C229C0",
    [MarbleColor.Green02]: "#6DC61F",
    [MarbleColor.Green03]: "#21A979",
    [MarbleColor.Orange]: "#D57302",
    [MarbleColor.Brown]: "#7C3B02",
    [MarbleColor.Black]: "#3B4A70",
    [MarbleColor.White]: "#EAD0BE",
};
// Neutral slate for an unpainted authoring box (colour is seed-generated — shown only after Compile).
export const NEUTRAL_BOX_HEX = "#8A93A8";
// The mystery box's desaturated gray (mirrors export-boxes.js MYSTERY_HEX).
const MYSTERY_HEX = "#9498AD";
// Tunnel / gate housing colours (mirror the export defaults).
const TUNNEL_HEX = "#9AA0AD";
const GATE_BAR_HEX = "#6cbf2b";
const GATE_EDGE_HEX = "#c62f22";
export function colorHexOf(color) {
    return MARBLE_COLOR_HEX[color] ?? NEUTRAL_BOX_HEX;
}
// --- closed box (top-down lidded box) — mirrors boxes-closed.html / export-boxes.js drawClosed ---------
const boxOpts = { cornerR: 0.2, baseTop: 0.04, baseBottom: -0.16, baseRim: 0.18, lidInset: 0.07, lidTop: 0.14, lidBottom: -0.05, seam: 0.3, rim: 0.28, ribs: 4 };
export function drawClosedBox(ctx, S, hex) {
    const o = boxOpts;
    const m = S * 0.045;
    const R = S * o.cornerR;
    roundRect(ctx, m, m, S - 2 * m, S - 2 * m, R);
    let g = ctx.createLinearGradient(0, m, 0, S - m);
    g.addColorStop(0, shade(hex, o.baseTop));
    g.addColorStop(1, shade(hex, o.baseBottom));
    ctx.fillStyle = g;
    ctx.fill();
    ctx.save();
    roundRect(ctx, m, m, S - 2 * m, S - 2 * m, R);
    ctx.clip();
    const vg = ctx.createRadialGradient(S / 2, S * 0.42, S * 0.25, S / 2, S / 2, S * 0.62);
    vg.addColorStop(0, "rgba(0,0,0,0)");
    vg.addColorStop(1, "rgba(0,0,0,0.16)");
    ctx.fillStyle = vg;
    ctx.fillRect(0, 0, S, S);
    ctx.restore();
    const li = S * o.lidInset;
    const lx = m + li;
    const lw = S - 2 * (m + li);
    const lr = Math.max(2, R - li * 0.5);
    roundRect(ctx, lx - S * 0.005, lx - S * 0.005, lw + S * 0.01, lw + S * 0.01, lr + S * 0.005);
    ctx.fillStyle = `rgba(0,0,0,${o.seam})`;
    ctx.fill();
    roundRect(ctx, lx, lx, lw, lw, lr);
    g = ctx.createLinearGradient(0, lx, 0, lx + lw);
    g.addColorStop(0, shade(hex, o.lidTop));
    g.addColorStop(1, shade(hex, o.lidBottom));
    ctx.fillStyle = g;
    ctx.fill();
    ctx.save();
    roundRect(ctx, lx, lx, lw, lw, lr);
    ctx.clip();
    const sh = ctx.createLinearGradient(0, lx, 0, lx + lw * 0.5);
    sh.addColorStop(0, "rgba(255,255,255,0.20)");
    sh.addColorStop(1, "rgba(255,255,255,0)");
    ctx.fillStyle = sh;
    ctx.fillRect(0, 0, S, S);
    if (o.ribs > 0) {
        const n = Math.round(o.ribs);
        const step = lw / n;
        const gw = Math.max(1, S * 0.006);
        for (let i = 1; i < n; i++) {
            const yy = lx + step * i;
            ctx.strokeStyle = "rgba(0,0,0,0.10)";
            ctx.lineWidth = gw;
            ctx.beginPath();
            ctx.moveTo(lx, yy);
            ctx.lineTo(lx + lw, yy);
            ctx.stroke();
        }
    }
    ctx.restore();
    if (o.rim > 0) {
        ctx.lineWidth = Math.max(1, S * 0.01);
        ctx.strokeStyle = `rgba(255,255,255,${o.rim})`;
        roundRect(ctx, lx + S * 0.012, lx + S * 0.012, lw - S * 0.024, lw - S * 0.024, Math.max(1, lr - S * 0.012));
        ctx.stroke();
    }
}
// --- "?" glyph — mirrors question.html / export-question.js drawQuestion --------------------------------
export function drawQuestion(ctx, S) {
    const cx = S * 0.5;
    const cy = S * 0.515;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.font = `bold ${Math.round(S * 0.5)}px "Segoe UI", Arial, sans-serif`;
    ctx.fillStyle = "rgba(0,0,0,0.30)";
    ctx.fillText("?", cx + S * 0.012, cy + S * 0.018);
    ctx.fillStyle = "#F4F2FB";
    ctx.fillText("?", cx, cy);
}
// --- arrow (pointing up) — mirrors arrow.html / export-arrow.js drawArrow -------------------------------
const arrowOpts = { color: "#F4F2FB", topY: 0.14, headHeight: 0.36, headWidth: 0.56, stemWidth: 0.22, stemBottomY: 0.84, round: 0.06, shadow: 0.3 };
function arrowPath(ctx, S, dx, dy) {
    const o = arrowOpts;
    const cx = S * 0.5 + dx;
    const topY = S * o.topY + dy;
    const headBaseY = topY + S * o.headHeight;
    const botY = S * o.stemBottomY + dy;
    const hw = S * o.headWidth;
    const sw = S * o.stemWidth;
    ctx.beginPath();
    ctx.moveTo(cx, topY);
    ctx.lineTo(cx - hw / 2, headBaseY);
    ctx.lineTo(cx - sw / 2, headBaseY);
    ctx.lineTo(cx - sw / 2, botY);
    ctx.lineTo(cx + sw / 2, botY);
    ctx.lineTo(cx + sw / 2, headBaseY);
    ctx.lineTo(cx + hw / 2, headBaseY);
    ctx.closePath();
}
export function drawArrow(ctx, S) {
    const o = arrowOpts;
    const lw = S * o.round;
    ctx.lineJoin = "round";
    ctx.lineCap = "round";
    arrowPath(ctx, S, S * 0.012, S * 0.02);
    ctx.fillStyle = `rgba(0,0,0,${o.shadow})`;
    ctx.strokeStyle = `rgba(0,0,0,${o.shadow})`;
    ctx.lineWidth = lw;
    ctx.fill();
    ctx.stroke();
    arrowPath(ctx, S, 0, 0);
    ctx.fillStyle = o.color;
    ctx.strokeStyle = o.color;
    ctx.lineWidth = lw;
    ctx.fill();
    ctx.stroke();
}
// --- tunnel (pipe mouth opening up) — mirrors tunnel.html / export-tunnel.js drawTunnel ----------------
const tunOpts = { cornerR: 0.2, baseTop: 0.06, baseBottom: -0.14, baseRim: 0.2, pipeDiameter: 0.64, pipeFlatten: 0.58, pipeCenterY: 0.46, pipeWall: 0.17, pipeDepth: 0.12, mouthDark: 0.6, rimLight: 0.45 };
export function drawTunnel(ctx, S) {
    const o = tunOpts;
    const hex = TUNNEL_HEX;
    const m = S * 0.045;
    const R = S * o.cornerR;
    roundRect(ctx, m, m, S - 2 * m, S - 2 * m, R);
    let g = ctx.createLinearGradient(0, m, 0, S - m);
    g.addColorStop(0, shade(hex, o.baseTop));
    g.addColorStop(1, shade(hex, o.baseBottom));
    ctx.fillStyle = g;
    ctx.fill();
    ctx.save();
    roundRect(ctx, m, m, S - 2 * m, S - 2 * m, R);
    ctx.clip();
    const vg = ctx.createRadialGradient(S / 2, S * 0.5, S * 0.2, S / 2, S / 2, S * 0.66);
    vg.addColorStop(0, "rgba(0,0,0,0)");
    vg.addColorStop(1, "rgba(0,0,0,0.18)");
    ctx.fillStyle = vg;
    ctx.fillRect(0, 0, S, S);
    ctx.restore();
    const cx = S * 0.5;
    const cy = S * o.pipeCenterY;
    const rx = S * o.pipeDiameter * 0.5;
    const ry = rx * o.pipeFlatten;
    const lip = Math.max(S * 0.02, S * o.pipeDepth);
    ctx.save();
    ctx.beginPath();
    ctx.rect(cx - rx, cy, rx * 2, lip);
    ctx.ellipse(cx, cy + lip, rx, ry, 0, 0, Math.PI * 2);
    ctx.clip();
    const wall = ctx.createLinearGradient(cx - rx, 0, cx + rx, 0);
    wall.addColorStop(0, shade(hex, -0.34));
    wall.addColorStop(0.45, shade(hex, -0.06));
    wall.addColorStop(1, shade(hex, -0.34));
    ctx.fillStyle = wall;
    ctx.fillRect(cx - rx, cy - ry, rx * 2, lip + ry * 3);
    ctx.restore();
    const rimGrad = ctx.createLinearGradient(0, cy - ry, 0, cy + ry);
    rimGrad.addColorStop(0, shade(hex, 0.16));
    rimGrad.addColorStop(1, shade(hex, -0.14));
    ctx.fillStyle = rimGrad;
    ellipse(ctx, cx, cy, rx, ry);
    ctx.fill();
    const irx = rx * (1 - o.pipeWall);
    const iry = ry * (1 - o.pipeWall);
    const hole = ctx.createLinearGradient(0, cy - iry, 0, cy + iry);
    hole.addColorStop(0, shade(hex, -o.mouthDark * 0.6));
    hole.addColorStop(1, shade(hex, -o.mouthDark));
    ctx.fillStyle = hole;
    ellipse(ctx, cx, cy, irx, iry);
    ctx.fill();
    ctx.lineWidth = Math.max(1, S * 0.016);
    ctx.strokeStyle = "rgba(0,0,0,0.35)";
    ctx.beginPath();
    ctx.ellipse(cx, cy, irx, iry, 0, 0, Math.PI * 2);
    ctx.stroke();
    if (o.rimLight > 0) {
        ctx.lineWidth = Math.max(1, S * 0.022);
        ctx.strokeStyle = `rgba(255,255,255,${o.rimLight})`;
        ctx.beginPath();
        ctx.ellipse(cx, cy, rx * 0.99, ry * 0.99, 0, Math.PI * 1.08, Math.PI * 1.92);
        ctx.stroke();
    }
}
// --- gate (two posts + glossy bar) — mirrors gate.html / export-gate.js drawGate -----------------------
const gateOpts = { postW: 0.15, postH: 0.8, postR: 0.135, postInset: 0.035, barH: 0.4, barR: 0.02, sheen: 0.3, topLight: 0.18, botDark: -0.2, outline: 0.38, outlineW: 0.022 };
function glossPiece(ctx, S, x, y, w, h, r, fill) {
    const o = gateOpts;
    roundRect(ctx, x, y, w, h, r);
    const g = ctx.createLinearGradient(0, y, 0, y + h);
    g.addColorStop(0, shade(fill, o.topLight));
    g.addColorStop(1, shade(fill, o.botDark));
    ctx.fillStyle = g;
    ctx.fill();
    if (o.sheen > 0) {
        ctx.save();
        roundRect(ctx, x, y, w, h, r);
        ctx.clip();
        const sh = ctx.createLinearGradient(0, y, 0, y + h * 0.55);
        sh.addColorStop(0, `rgba(255,255,255,${o.sheen})`);
        sh.addColorStop(1, "rgba(255,255,255,0)");
        ctx.fillStyle = sh;
        ctx.fillRect(x, y, w, h);
        ctx.restore();
    }
    if (o.outline > 0 && o.outlineW > 0) {
        ctx.lineWidth = Math.max(1, S * o.outlineW);
        ctx.strokeStyle = shade(fill, -o.outline);
        roundRect(ctx, x + ctx.lineWidth * 0.5, y + ctx.lineWidth * 0.5, w - ctx.lineWidth, h - ctx.lineWidth, r);
        ctx.stroke();
    }
}
export function drawGate(ctx, S) {
    drawGateWide(ctx, 0, 0, S, S);
}
// A gate stretched across an arbitrary rectangle (absolute coords): the two end-posts stay sized to the
// cell HEIGHT `h`, while the bar spans the full WIDTH `w`. Used so a multi-cell gate is one wide sprite
// (posts at each end, bar scaled across) rather than a sprite duplicated per cell.
export function drawGateWide(ctx, x, y, w, h) {
    if (drawGateSprite(ctx, x, y, w, h))
        return;
    const o = gateOpts;
    const pw = h * o.postW;
    const ph = h * o.postH;
    const pr = h * o.postR;
    const leftX = x + h * o.postInset;
    const rightX = x + w - h * o.postInset - pw;
    const postY = y + (h - ph) / 2;
    const bh = h * o.barH;
    const br = h * o.barR;
    const barLeft = leftX + pw * 0.5;
    const barRight = rightX + pw * 0.5;
    const barY = y + (h - bh) / 2;
    glossPiece(ctx, h, barLeft, barY, barRight - barLeft, bh, br, GATE_BAR_HEX);
    glossPiece(ctx, h, leftX, postY, pw, ph, pr, GATE_EDGE_HEX);
    glossPiece(ctx, h, rightX, postY, pw, ph, pr, GATE_EDGE_HEX);
}
// The game's gate (Gate.Build): the bar 9-sliced across its span — end posts fixed (the sprite's 46/47 px
// borders), the middle stretched — 134 px tall, centred on the span. Two cells wide or fewer draws the art
// at its natural 301 px: the art is drawn for two cells, and a one-cell gate keeps that size, overhanging
// its neighbours evenly. `h` is one cell; `w` the span.
const GATE_BORDER_L = 46;
const GATE_BORDER_R = 47;
const GATE_NATURAL_W = 301;
const GATE_H = 134;
function drawGateSprite(ctx, x, y, w, h) {
    const img = Sprites.single("gate");
    if (!img)
        return false;
    const k = h / CELL_PX;
    const cells = Math.round(w / h);
    const drawW = (cells <= 2 ? GATE_NATURAL_W : cells * CELL_PX) * k;
    const drawH = GATE_H * k;
    const left = x + w / 2 - drawW / 2;
    const top = y + h / 2 - drawH / 2;
    const iw = img.naturalWidth;
    const ih = img.naturalHeight;
    const bl = GATE_BORDER_L * k;
    const br = GATE_BORDER_R * k;
    ctx.drawImage(img, 0, 0, GATE_BORDER_L, ih, left, top, bl, drawH);
    ctx.drawImage(img, GATE_BORDER_L, 0, iw - GATE_BORDER_L - GATE_BORDER_R, ih, left + bl, top, Math.max(0, drawW - bl - br), drawH);
    ctx.drawImage(img, iw - GATE_BORDER_R, 0, GATE_BORDER_R, ih, left + drawW - br, top, br, drawH);
    return true;
}
// --- modifier overlays (absolute canvas coords, since modifiers span multiple cells) ------------------
const METAL_HEX = "#9AA0AD";
// A domed corner screw/bolt with a phillips slot.
function drawScrew(ctx, x, y, R) {
    ctx.beginPath();
    ctx.arc(x, y, R * 1.28, 0, Math.PI * 2);
    ctx.fillStyle = shade(METAL_HEX, -0.3);
    ctx.fill();
    const g = ctx.createRadialGradient(x - R * 0.35, y - R * 0.4, R * 0.15, x, y, R * 1.1);
    g.addColorStop(0, shade(METAL_HEX, 0.2));
    g.addColorStop(1, shade(METAL_HEX, -0.14));
    ctx.beginPath();
    ctx.arc(x, y, R, 0, Math.PI * 2);
    ctx.fillStyle = g;
    ctx.fill();
    ctx.strokeStyle = "rgba(0,0,0,0.4)";
    ctx.lineWidth = Math.max(1, R * 0.26);
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.moveTo(x - R * 0.6, y);
    ctx.lineTo(x + R * 0.6, y);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(x, y - R * 0.6);
    ctx.lineTo(x, y + R * 0.6);
    ctx.stroke();
}
// A metal connector bar between two cell centres, with a bolt at each end (mirrors the connector art).
export function drawConnectorBar(ctx, x1, y1, x2, y2, thickness) {
    if (drawConnectorSprite(ctx, x1, y1, x2, y2))
        return;
    ctx.save();
    const angle = Math.atan2(y2 - y1, x2 - x1);
    const len = Math.hypot(x2 - x1, y2 - y1);
    ctx.translate(x1, y1);
    ctx.rotate(angle);
    const th = thickness;
    const r = th * 0.5;
    roundRect(ctx, 0, -th / 2, len, th, r);
    const g = ctx.createLinearGradient(0, -th / 2, 0, th / 2);
    g.addColorStop(0, shade(METAL_HEX, 0.14));
    g.addColorStop(1, shade(METAL_HEX, -0.16));
    ctx.fillStyle = g;
    ctx.fill();
    ctx.restore();
    drawScrew(ctx, x1, y1, thickness * 0.3);
    drawScrew(ctx, x2, y2, thickness * 0.3);
}
// The game's joint (Connector.Build): on the shared border, never rotated. The art is named for the JOINT'S
// own standing direction, perpendicular to the pair — a side-by-side pair wears the tall vertical joint.
// The cell size is the distance between the two centres (the pair is always adjacent).
function drawConnectorSprite(ctx, x1, y1, x2, y2) {
    const sideBySide = Math.abs(x2 - x1) >= Math.abs(y2 - y1);
    const img = Sprites.single(sideBySide ? "connectorVertical" : "connectorHorizontal");
    if (!img)
        return false;
    const k = Math.hypot(x2 - x1, y2 - y1) / CELL_PX;
    blit(ctx, img, (x1 + x2) / 2, (y1 + y2) / 2, k);
    return true;
}
// The game's lock panel (LockPanelModifier.Build): the plate stretched to cover the whole area — a 3x3 is
// exactly the art's authored size — over its soft shadow, which is sized a margin larger (46 x 45 px).
function drawMetalRectSprite(ctx, x, y, w, h, cell) {
    const plate = Sprites.single("metalPlate");
    if (!plate)
        return false;
    const k = cell / CELL_PX;
    const cx = x + w / 2;
    const cy = y + h / 2;
    const shadow = Sprites.single("metalPlateShadow");
    if (shadow)
        blit(ctx, shadow, cx, cy, 1, 0, w + 46 * k, h + 45 * k);
    blit(ctx, plate, cx, cy, 1, 0, w, h);
    return true;
}
// A metal lock-panel plate over a rectangle, with 4 corner screws + the counter number.
export function drawMetalRect(ctx, x, y, w, h, counter, cell) {
    if (cell !== undefined && drawMetalRectSprite(ctx, x, y, w, h, cell)) {
        drawCounter(ctx, x, y, w, h, counter);
        return;
    }
    const min = Math.min(w, h);
    const m = min * 0.06;
    const R = min * 0.14;
    roundRect(ctx, x + m, y + m, w - 2 * m, h - 2 * m, R);
    const g = ctx.createLinearGradient(0, y + m, 0, y + h - m);
    g.addColorStop(0, shade(METAL_HEX, 0.14));
    g.addColorStop(1, shade(METAL_HEX, -0.16));
    ctx.fillStyle = g;
    ctx.fill();
    ctx.save();
    roundRect(ctx, x + m, y + m, w - 2 * m, h - 2 * m, R);
    ctx.clip();
    const sh = ctx.createLinearGradient(0, y + m, 0, y + m + h * 0.5);
    sh.addColorStop(0, "rgba(255,255,255,0.20)");
    sh.addColorStop(1, "rgba(255,255,255,0)");
    ctx.fillStyle = sh;
    ctx.fillRect(x, y, w, h);
    ctx.restore();
    ctx.lineWidth = Math.max(1, min * 0.02);
    ctx.strokeStyle = "rgba(0,0,0,0.28)";
    roundRect(ctx, x + m, y + m, w - 2 * m, h - 2 * m, R);
    ctx.stroke();
    const inset = min * 0.16;
    const sr = min * 0.08;
    drawScrew(ctx, x + m + inset, y + m + inset, sr);
    drawScrew(ctx, x + w - m - inset, y + m + inset, sr);
    drawScrew(ctx, x + m + inset, y + h - m - inset, sr);
    drawScrew(ctx, x + w - m - inset, y + h - m - inset, sr);
    drawCounter(ctx, x, y, w, h, counter);
}
// The panel's counter: how many drops it takes to lift. Outlined, so it reads on the light game plate too.
function drawCounter(ctx, x, y, w, h, counter) {
    if (counter <= 0)
        return;
    const min = Math.min(w, h);
    ctx.save();
    ctx.globalAlpha = 1; // readable over a faded panel, like drawTag
    ctx.font = `bold ${Math.round(min * 0.3)}px "Segoe UI", Arial, sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.lineJoin = "round";
    ctx.lineWidth = Math.max(2, min * 0.04);
    ctx.strokeStyle = "rgba(40,30,110,0.9)";
    ctx.strokeText(String(counter), x + w / 2, y + h / 2);
    ctx.fillStyle = "#fff";
    ctx.fillText(String(counter), x + w / 2, y + h / 2);
    ctx.restore();
}
// --- empty board slot (subtle recessed square, so the board reads as a grid) ---------------------------
export function drawSlot(ctx, S) {
    const m = S * 0.06;
    roundRect(ctx, m, m, S - 2 * m, S - 2 * m, S * 0.16);
    ctx.fillStyle = "rgba(255,255,255,0.04)";
    ctx.fill();
    ctx.lineWidth = Math.max(1, S * 0.012);
    ctx.strokeStyle = "rgba(255,255,255,0.06)";
    ctx.stroke();
}
// --- cleared cell (board ground with nothing on it) ------------------------------------------------------
// Has to read as "this is board, and it is empty" — so neither a box nor the bare slot every empty cell
// shows. A tinted floor with a dashed edge and faint hatching: the edge says the wall will stop here, the
// empty middle says nothing stands on it.
export function drawCleared(ctx, S) {
    const m = S * 0.06;
    const r = S * 0.16;
    ctx.save();
    roundRect(ctx, m, m, S - 2 * m, S - 2 * m, r);
    ctx.fillStyle = "rgba(148,163,184,0.16)";
    ctx.fill();
    ctx.clip();
    ctx.lineWidth = Math.max(1, S * 0.012);
    ctx.strokeStyle = "rgba(148,163,184,0.18)";
    const gap = S * 0.14;
    for (let d = -S; d < S; d += gap) {
        ctx.beginPath();
        ctx.moveTo(d, S);
        ctx.lineTo(d + S, 0);
        ctx.stroke();
    }
    ctx.restore();
    ctx.save();
    roundRect(ctx, m, m, S - 2 * m, S - 2 * m, r);
    ctx.setLineDash([S * 0.07, S * 0.05]);
    ctx.lineWidth = Math.max(1.5, S * 0.025);
    ctx.strokeStyle = "rgba(203,213,225,0.7)";
    ctx.stroke();
    ctx.restore();
}
// --- game sprites ---------------------------------------------------------------------------------------
// Offsets below are the game prefab's, in SPRITE pixels with y DOWN (Unity's y-up world units x -100).
// Draws `img` at its natural size times `k`, centred on (cx, cy), optionally turned by `angle` radians
// (clockwise on screen) about that centre, and stretched to `w` x `h` sprite pixels when given.
function blit(ctx, img, cx, cy, k, angle = 0, w, h) {
    const dw = (w ?? img.naturalWidth) * k;
    const dh = (h ?? img.naturalHeight) * k;
    if (angle === 0) {
        ctx.drawImage(img, cx - dw / 2, cy - dh / 2, dw, dh);
        return;
    }
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(angle);
    ctx.drawImage(img, -dw / 2, -dh / 2, dw, dh);
    ctx.restore();
}
// The game's tunnel pipe and arrow mark are both authored pointing DOWN and turned by Unity z-degrees
// (Tunnel.ZForDirection / MarbleBox.ZForArrow): Top 180, Right 90, Bottom 0, Left -90. Unity's z turns
// counter-clockwise with y up; the canvas turns clockwise with y down — the same picture, sign flipped.
function downArtAngle(dir) {
    switch (dir) {
        case GridDirection.Top: return -Math.PI;
        case GridDirection.Right: return -Math.PI / 2;
        case GridDirection.Bottom: return 0;
        case GridDirection.Left: return Math.PI / 2;
        default: return -Math.PI;
    }
}
// Box.prefab: the lid (BoxClosed) sits 3.7 px low, the screw socket (ScrewIcon) 2.5 px high — the centre
// of the lid's face — and the arrow mark there too (MarbleBox.BuildArrowMark). BoxBase.prefab: the socket plate (EmptySpace) 6.6 px low at 1.11x.
const BOX_LID_DY = 3.7;
const BOX_PIT_DY = -2.5;
const BOX_ARROW_SCALE = 0.95;
const BOX_BASE_DY = 6.6;
const BOX_BASE_SCALE = 1.11;
// A closed box as the game stands it: base plate, lid, then the connect cover (a linked pair), then the
// arrow mark or the screw socket. The socket only goes on a lid with room for it — never on an arrow or a
// mystery lid (MarbleBox.ShowsScrewIcon). False if the sprites have not loaded yet.
function drawBoxSprite(ctx, S, tint, mystery, arrow, connected, split = null, noPit = false) {
    const lid = mystery ? Sprites.single("boxMystery") : Sprites.boxClosed(tint);
    if (!lid)
        return false;
    const k = S / CELL_PX;
    const c = S / 2;
    const base = Sprites.single("emptySpace");
    if (base)
        blit(ctx, base, c, c + BOX_BASE_DY * k, k * BOX_BASE_SCALE);
    const splitArt = split && !mystery ? splitLid(split) : null;
    if (splitArt) {
        // A split box's lid in its three colours, built exactly as the game's shader draws it (art/splitLid.ts).
        const w = splitArt.width * k;
        const h = splitArt.height * k;
        ctx.drawImage(splitArt, c - w / 2, c + BOX_LID_DY * k - h / 2, w, h);
    }
    else if (split && !mystery) {
        // Until the index sprite and ramps decode: each colour's own lid art, clipped to its column.
        inSplitColumns(ctx, S, (col) => {
            const part = Sprites.boxClosed(split[col]) ?? lid;
            blit(ctx, part, c, c + BOX_LID_DY * k, k);
        });
    }
    else {
        blit(ctx, lid, c, c + BOX_LID_DY * k, k);
    }
    if (connected) {
        const cover = Sprites.single("connectorBoxCover");
        if (cover)
            blit(ctx, cover, c, c + BOX_LID_DY * k, k);
    }
    if (arrow !== null) {
        const mark = Sprites.arrow(tint);
        // The game drops the UP arrow 1 px below the others (MarbleBox.UpArrowDropY) and draws every
        // arrow at 0.95 (Box.prefab ArrowMark scale).
        const dy = BOX_PIT_DY + (arrow === GridDirection.Top ? 1 : 0);
        if (mark)
            blit(ctx, mark, c, c + dy * k, k * BOX_ARROW_SCALE, downArtAngle(arrow));
    }
    else if (!mystery && !noPit) {
        // No socket on a split box's lid: it carries three colours and has no screw art of its own
        // (MarbleBox.ShowsScrewIcon).
        const pit = Sprites.boxPit(tint);
        if (pit)
            blit(ctx, pit, c, c + BOX_PIT_DY * k, k);
    }
    return true;
}
// The closed double box's layout, fixed at the game's (DoubleBoxLayout, measured on art-gen/double-box-v2.html).
// The double box art is the plain box's own size, centred where a plain box's tray and lid are.
class DoubleBoxArt {
    constructor() { }
}
// InnerLidScale / InnerLidDrop: the closed box inside, against an ordinary closed box, and how far (px) its
// centre sits below the frame's.
DoubleBoxArt.LID_SCALE = 1.05;
DoubleBoxArt.LID_DROP = 2.85;
// The double box (Box.Double), as the game stands it at level start: the closed box of the inner colour, a little
// bigger and lower than an ordinary one, with its screw socket; the groove line over it; and the frame of the outer
// colour round it, centred where an ordinary box's lid is. No drop shadow: the board draws none under any box.
function drawDoubleBoxSprite(ctx, S, outer, inner) {
    const frame = Sprites.boxDoubleFrame(outer);
    const groove = Sprites.single("boxDoubleGroove");
    const lid = Sprites.boxClosed(inner);
    if (!frame || !groove || !lid)
        return false;
    const k = S / CELL_PX;
    const c = S / 2;
    const base = Sprites.single("emptySpace");
    if (base)
        blit(ctx, base, c, c + BOX_BASE_DY * k, k * BOX_BASE_SCALE);
    // The box inside: its lid and socket scaled about the lid's centre (Box.NestInner).
    const s = DoubleBoxArt.LID_SCALE;
    const lidY = BOX_LID_DY + DoubleBoxArt.LID_DROP;
    blit(ctx, lid, c, c + lidY * k, k * s);
    const pit = Sprites.boxPit(inner);
    if (pit)
        blit(ctx, pit, c, c + (lidY + (BOX_PIT_DY - BOX_LID_DY) * s) * k, k * s);
    blit(ctx, groove, c, c + BOX_LID_DY * k, k);
    blit(ctx, frame, c, c + BOX_LID_DY * k, k);
    return true;
}
const JACK_CAP_DX = -0.5;
const JACK_CAP_DY = -14.8;
const JACK_PATCH_DX = -0.5;
const JACK_PATCH_DY = -1.3;
const JACK_BUTTON_DX = -1;
const JACK_BUTTON_DY = 57.2;
function drawJumpingJackSprite(ctx, S, tint, mystery) {
    const body = mystery ? Sprites.single("jackMysteryBody") : Sprites.jackBody(tint);
    const cap = mystery ? Sprites.single("jackMysteryCap") : Sprites.jackClosedCap(tint);
    const patch = Sprites.single("itemJumpingJackBodyPatch");
    const button = Sprites.single(mystery ? "jackMysteryButton" : "itemJumpingJackButtonPatch");
    if (!body || !cap || !patch || !button)
        return false;
    const k = S / CELL_PX;
    const c = S / 2;
    const base = Sprites.single("emptySpace");
    if (base)
        blit(ctx, base, c, c + BOX_BASE_DY * k, k * BOX_BASE_SCALE);
    blit(ctx, body, c, c + BOX_LID_DY * k, k);
    blit(ctx, cap, c + JACK_CAP_DX * k, c + JACK_CAP_DY * k, k);
    blit(ctx, patch, c + JACK_PATCH_DX * k, c + JACK_PATCH_DY * k, k);
    blit(ctx, button, c + JACK_BUTTON_DX * k, c + JACK_BUTTON_DY * k, k);
    return true;
}
// Until the double box art decodes: the outer box with a smaller box of the inner colour drawn on it.
function drawDoubleBoxFallback(ctx, S, outerHex, innerHex) {
    drawClosedBox(ctx, S, outerHex);
    ctx.save();
    ctx.translate(S / 2, S / 2);
    ctx.scale(0.72, 0.72);
    ctx.translate(-S / 2, -S / 2);
    drawClosedBox(ctx, S, innerHex);
    ctx.restore();
}
// Runs `draw(column)` three times, clipped to the box's left, middle and right column in turn.
function inSplitColumns(ctx, S, draw) {
    const b = SPLIT_BOUNDARY * (S / CELL_PX);
    const c = S / 2;
    const edges = [-S, c - b, c + b, 2 * S];
    for (let col = 0; col < 3; col++) {
        ctx.save();
        ctx.beginPath();
        ctx.rect(edges[col], -S, edges[col + 1] - edges[col], 3 * S);
        ctx.clip();
        draw(col);
        ctx.restore();
    }
}
// The two seams of a split box whose colours are not dealt yet, so it reads as split on an authoring board
// (where every box is grey). Drawn over the lid, dark with a light edge so it shows on grey and on "?" alike.
function drawSplitSeams(ctx, S) {
    const k = S / CELL_PX;
    const c = S / 2;
    const b = SPLIT_BOUNDARY * k;
    const top = c + (BOX_LID_DY - 52) * k;
    const bottom = c + (BOX_LID_DY + 52) * k;
    ctx.save();
    ctx.lineCap = "round";
    for (const x of [c - b, c + b]) {
        ctx.strokeStyle = "rgba(255,255,255,0.55)";
        ctx.lineWidth = Math.max(2, 4.5 * k);
        ctx.beginPath();
        ctx.moveTo(x, top);
        ctx.lineTo(x, bottom);
        ctx.stroke();
        ctx.strokeStyle = "rgba(15,23,42,0.75)";
        ctx.lineWidth = Math.max(1, 2.2 * k);
        ctx.beginPath();
        ctx.moveTo(x, top);
        ctx.lineTo(x, bottom);
        ctx.stroke();
    }
    ctx.restore();
}
// Tunnel.prefab: the pipe on the cell centre with its shadow 7.7 px below it, both turned together.
function drawTunnelSprite(ctx, S, dir) {
    const pipe = Sprites.single("tunnel");
    if (!pipe)
        return false;
    const k = S / CELL_PX;
    ctx.save();
    ctx.translate(S / 2, S / 2);
    ctx.rotate(downArtAngle(dir));
    const shadow = Sprites.single("tunnelShadow");
    if (shadow)
        blit(ctx, shadow, 0, 7.7 * k, k);
    blit(ctx, pipe, 0, 0, k);
    ctx.restore();
    return true;
}
// Rotates the canvas so a canonical "up/horizontal" sprite points in `dir`, runs `draw`, then restores.
function withDirection(ctx, S, dir, draw) {
    let angle = 0;
    if (dir === GridDirection.Right)
        angle = Math.PI / 2;
    else if (dir === GridDirection.Bottom)
        angle = Math.PI;
    else if (dir === GridDirection.Left)
        angle = -Math.PI / 2;
    if (angle === 0) {
        draw();
        return;
    }
    ctx.save();
    ctx.translate(S / 2, S / 2);
    ctx.rotate(angle);
    ctx.translate(-S / 2, -S / 2);
    draw();
    ctx.restore();
}
/**
 * Draws one editor cell into [0,S]x[0,S] (the grid translates per cell). `preview` is the compiled
 * colour, or null while authoring (colours are seed-generated): a plain box shows the grey box, a
 * mystery box shows the "?" lid — and in preview both reveal the real colour. `connected` puts the
 * connect cover on the lid, as the game does for both boxes of a linked pair.
 */
export function drawEditorCell(ctx, S, cell, preview, connected = false, 
// A split box's compiled column colours, left to right (CellData.splitColors); null while authoring.
splitPreview = null, 
// A double box's compiled inside colour (CellData.innerColor); null while authoring.
innerPreview = null) {
    const colorHex = preview !== null ? colorHexOf(preview) : null;
    drawSlot(ctx, S);
    // Checked first because it wins over every other flag, exactly as the compiler treats it.
    if (cell.isCleared) {
        drawCleared(ctx, S);
        return;
    }
    if (cell.isGate) {
        drawGate(ctx, S);
        return;
    }
    if (cell.isTunnel) {
        if (!drawTunnelSprite(ctx, S, cell.tunnelDirection)) {
            withDirection(ctx, S, cell.tunnelDirection, () => drawTunnel(ctx, S));
        }
        return;
    }
    if (cell.isSuperTunnel) {
        drawSuperTunnel(ctx, S, cell.superTunnelSpawnCount ?? 0);
        return;
    }
    if (cell.isToolBox) {
        drawToolBox(ctx, S, cell.toolBoxSpawnCount ?? 0);
        return;
    }
    if (cell.isPipe) {
        // One cell on its own: the head with the collar right above it (the board draws the full column,
        // see drawPipe - GridCanvas skips this for pipes).
        drawPipeIcon(ctx, S, cell.pipeSpawnCount ?? 0);
        return;
    }
    if (cell.isPropeller) {
        drawPropeller(ctx, S, cell.propellerDirection ?? GridDirection.Top);
        if (cell.propellerHasBox) {
            drawHiddenBoxChip(ctx, S, colorHex ?? NEUTRAL_BOX_HEX);
        }
        return;
    }
    const tint = preview !== null ? preview : NEUTRAL;
    // A double box is a plain box holding another — the compiler drops any other box flag on it, so none is
    // drawn. Grey on grey while authoring (the tray and rim are what tell it from a plain box), both colours
    // once compiled.
    if (cell.isDouble) {
        const inner = preview !== null && innerPreview !== null ? innerPreview : NEUTRAL;
        if (!drawDoubleBoxSprite(ctx, S, tint, inner)) {
            drawDoubleBoxFallback(ctx, S, colorHex ?? NEUTRAL_BOX_HEX, inner === NEUTRAL ? NEUTRAL_BOX_HEX : colorHexOf(inner));
        }
        return;
    }
    if (cell.isJumpingJack && !cell.isSplit) {
        if (!drawJumpingJackSprite(ctx, S, tint, preview === null && cell.isMystery)) {
            drawClosedBox(ctx, S, colorHex ?? NEUTRAL_BOX_HEX);
        }
        return;
    }
    const mystery = preview === null && cell.isMystery;
    const split = cell.isSplit ?? false;
    const columns = split && preview !== null && splitPreview && splitPreview.length >= 3 ? splitPreview : null;
    if (!drawBoxSprite(ctx, S, tint, mystery, cell.isArrow ? cell.arrowDirection : null, connected, columns, split)) {
        if (columns) {
            inSplitColumns(ctx, S, (col) => drawClosedBox(ctx, S, colorHexOf(columns[col])));
        }
        else if (colorHex) {
            drawClosedBox(ctx, S, colorHex); // preview: revealed colour (even for mystery)
        }
        else if (cell.isMystery) {
            drawClosedBox(ctx, S, MYSTERY_HEX);
            drawQuestion(ctx, S);
        }
        else {
            drawClosedBox(ctx, S, NEUTRAL_BOX_HEX);
        }
        if (cell.isArrow) {
            withDirection(ctx, S, cell.arrowDirection, () => drawArrow(ctx, S));
        }
    }
    // Authoring: the colours are not dealt yet, so the seams are what tell a split box from a plain one.
    if (split && !columns) {
        drawSplitSeams(ctx, S);
    }
    if (cell.isGlide) {
        drawGlideWings(ctx, S);
    }
    if (cell.isDudBox && !split) {
        drawDudBadge(ctx, S);
    }
}
const DUD_BADGE_OFFSET = 56;
const DUD_BADGE_RADIUS = 26;
function drawDudBadge(ctx, S) {
    const k = S / CELL_PX;
    const cx = S / 2 + DUD_BADGE_OFFSET * k;
    const cy = S / 2 - DUD_BADGE_OFFSET * k;
    const r = DUD_BADGE_RADIUS * k;
    const arm = r * 0.44;
    ctx.save();
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fillStyle = "#3a3a40";
    ctx.fill();
    ctx.beginPath();
    ctx.arc(cx, cy, r * 0.84, 0, Math.PI * 2);
    ctx.fillStyle = "#9c9ca2";
    ctx.fill();
    ctx.beginPath();
    ctx.moveTo(cx - arm, cy - arm);
    ctx.lineTo(cx + arm, cy + arm);
    ctx.moveTo(cx + arm, cy - arm);
    ctx.lineTo(cx - arm, cy + arm);
    ctx.strokeStyle = "#fff";
    ctx.lineWidth = r * 0.26;
    ctx.lineCap = "round";
    ctx.stroke();
    ctx.restore();
}
// The glide box's wings, as the game places them (MarbleBox.Glide: GlideWingX/Y/Scale, box-local units at
// 100 px per unit). The art is 300 px per unit, hence the third. Falls back to a small "GLIDE" tag until the
// sprite has decoded.
const GLIDE_WING_X = 74; // 0.74 units
const GLIDE_WING_Y = -30; // 0.30 units UP (canvas y grows downward)
const GLIDE_WING_SCALE = 0.85 / 3;
export function drawGlideWings(ctx, S) {
    const wing = Sprites.single("boxGlideWing");
    const k = S / CELL_PX;
    const c = S / 2;
    if (!wing) {
        drawTag(ctx, c, S * 0.16, S, "GLIDE");
        return;
    }
    const y = c + GLIDE_WING_Y * k; // from the box root, like the game (not the lid, which sits 3.7 px lower)
    blit(ctx, wing, c + GLIDE_WING_X * k, y, k * GLIDE_WING_SCALE);
    ctx.save();
    ctx.translate(c - GLIDE_WING_X * k, y);
    ctx.scale(-1, 1);
    blit(ctx, wing, 0, 0, k * GLIDE_WING_SCALE);
    ctx.restore();
}
// --- game-mechanics items -------------------------------------------------------------------------------
// Each draws its game sprites first (itemSprites.ts). The procedural looks here are the FALLBACK for the
// moment before those decode: placeholders with a TEXT label as well as a colour, since several are told
// apart by hue alone otherwise. The tags that carry editor-only facts (LASER n / KEY n, UNLOCK) stay on
// over the real art too.
// A short label in a dark pill, centred on (cx, cy). Sized off the cell so it stays legible at every zoom.
// Always fully opaque: the board fades the covers a tag sits on (see COVER_ALPHA in GridCanvas), and the tag
// is the part of a cover that must stay readable.
function drawTag(ctx, cx, cy, S, text) {
    ctx.save();
    ctx.globalAlpha = 1;
    ctx.font = `bold ${Math.max(8, Math.round(S * 0.15))}px "Segoe UI", Arial, sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    const w = ctx.measureText(text).width + S * 0.12;
    const h = S * 0.21;
    roundRect(ctx, cx - w / 2, cy - h / 2, w, h, h * 0.45);
    ctx.fillStyle = "rgba(15,23,42,0.82)";
    ctx.fill();
    ctx.fillStyle = "#fff";
    ctx.fillText(text, cx, cy + S * 0.005);
    ctx.restore();
}
// Super tunnel: the tunnel housing with its mouth in the MIDDLE and a chevron on every side — it serves all
// four neighbours, turning to whichever was dropped (TWGS.Gameplay SuperTunnel).
export function drawSuperTunnel(ctx, S, count = 0) {
    if (drawSuperTunnelSprite(ctx, S, count))
        return;
    const hex = TUNNEL_HEX;
    const m = S * 0.045;
    roundRect(ctx, m, m, S - 2 * m, S - 2 * m, S * 0.2);
    const g = ctx.createLinearGradient(0, m, 0, S - m);
    g.addColorStop(0, shade(hex, 0.02));
    g.addColorStop(1, shade(hex, -0.2));
    ctx.fillStyle = g;
    ctx.fill();
    const c = S / 2;
    const r = S * 0.2;
    ctx.beginPath();
    ctx.arc(c, c, r, 0, Math.PI * 2);
    ctx.fillStyle = shade(hex, 0.12);
    ctx.fill();
    ctx.beginPath();
    ctx.arc(c, c, r * 0.7, 0, Math.PI * 2);
    ctx.fillStyle = shade(hex, -0.55);
    ctx.fill();
    ctx.fillStyle = "#F4F2FB";
    for (let i = 0; i < 4; i++) {
        ctx.save();
        ctx.translate(c, c);
        ctx.rotate((Math.PI / 2) * i);
        ctx.beginPath();
        ctx.moveTo(0, -S * 0.43);
        ctx.lineTo(-S * 0.08, -S * 0.32);
        ctx.lineTo(S * 0.08, -S * 0.32);
        ctx.closePath();
        ctx.fill();
        ctx.restore();
    }
    drawTag(ctx, c, S * 0.8, S, "4-WAY");
}
// Ice over one box (absolute coords, cell of size S at x,y): a translucent pane the box reads through,
// cracked when it starts with one hit left.
export function drawIceCover(ctx, x, y, S, hits) {
    if (drawIceSprite(ctx, x, y, S, hits))
        return;
    const m = S * 0.06;
    ctx.save();
    roundRect(ctx, x + m, y + m, S - 2 * m, S - 2 * m, S * 0.18);
    const g = ctx.createLinearGradient(x, y, x + S, y + S);
    g.addColorStop(0, "rgba(224,247,255,0.78)");
    g.addColorStop(1, "rgba(125,196,230,0.62)");
    ctx.fillStyle = g;
    ctx.fill();
    ctx.lineWidth = Math.max(1.5, S * 0.03);
    ctx.strokeStyle = "rgba(255,255,255,0.95)";
    ctx.stroke();
    ctx.strokeStyle = "rgba(255,255,255,0.8)";
    ctx.lineWidth = Math.max(1, S * 0.025);
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.moveTo(x + S * 0.22, y + S * 0.36);
    ctx.lineTo(x + S * 0.36, y + S * 0.22);
    ctx.moveTo(x + S * 0.22, y + S * 0.5);
    ctx.lineTo(x + S * 0.5, y + S * 0.22);
    ctx.stroke();
    if (hits <= 1) {
        ctx.strokeStyle = "rgba(30,64,90,0.85)";
        ctx.lineWidth = Math.max(1, S * 0.022);
        ctx.beginPath();
        ctx.moveTo(x + S * 0.5, y + S * 0.12);
        ctx.lineTo(x + S * 0.56, y + S * 0.34);
        ctx.lineTo(x + S * 0.46, y + S * 0.48);
        ctx.lineTo(x + S * 0.62, y + S * 0.66);
        ctx.moveTo(x + S * 0.46, y + S * 0.48);
        ctx.lineTo(x + S * 0.3, y + S * 0.6);
        ctx.stroke();
    }
    ctx.restore();
    drawTag(ctx, x + S / 2, y + S * 0.8, S, `ICE ${hits}`);
}
// The super mystery box: an opaque lid that HIDES the box under it, so it is drawn over any preview colour.
export function drawSuperMysteryCover(ctx, x, y, S) {
    if (drawSuperMysterySprite(ctx, x, y, S))
        return;
    ctx.save();
    ctx.translate(x, y);
    drawClosedBox(ctx, S, "#5B3F8C");
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.font = `bold ${Math.round(S * 0.36)}px "Segoe UI", Arial, sans-serif`;
    ctx.fillStyle = "rgba(0,0,0,0.3)";
    ctx.fillText("??", S * 0.5 + S * 0.012, S * 0.45 + S * 0.018);
    ctx.fillStyle = "#FDE68A";
    ctx.fillText("??", S * 0.5, S * 0.45);
    ctx.restore();
    drawTag(ctx, x + S / 2, y + S * 0.8, S, "SUPER ?");
}
// The super mystery box as the BOARD shows it: the box underneath stays drawn in its own colour, and the lid is
// only a dashed purple frame with a "??" pip in the top-right corner. The real lid (drawSuperMysteryCover,
// still the tool icon) is an opaque purple box — faded, its purple mixed into the colour under it, so a blue
// box read as violet. The author places these to hide a colour; the editor is where that colour must show.
export function drawSuperMysteryMark(ctx, x, y, S) {
    const m = S * 0.05;
    ctx.save();
    roundRect(ctx, x + m, y + m, S - 2 * m, S - 2 * m, S * 0.16);
    ctx.setLineDash([S * 0.1, S * 0.06]);
    ctx.lineWidth = Math.max(2, S * 0.05);
    ctx.strokeStyle = "#A855F7";
    ctx.stroke();
    ctx.setLineDash([]);
    const r = Math.max(6, S * 0.15);
    const cx = x + S - r - S * 0.02;
    const cy = y + r + S * 0.02;
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fillStyle = "#5B3F8C";
    ctx.fill();
    ctx.lineWidth = Math.max(1, r * 0.18);
    ctx.strokeStyle = "rgba(255,255,255,0.9)";
    ctx.stroke();
    ctx.fillStyle = "#FDE68A";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.font = `bold ${Math.round(r * 1.05)}px "Segoe UI", Arial, sans-serif`;
    ctx.fillText("??", cx, cy + r * 0.05);
    ctx.restore();
    drawTag(ctx, x + S / 2, y + S * 0.8, S, "SUPER ?");
}
// A sliding door across `cells` (the run from the ANCHOR outward, in grid coords), of which the first
// `length` are covered. Drawn the way the game builds it: a housing on the anchor, a measuring-tape rail over
// the covered cells and a cap on the last one. Cells the door was authored across but starts retracted from
// are outlined only, so the author can see the run it will never reach.
export function drawSlidingDoor(ctx, cells, length, cs) {
    if (cells.length === 0) {
        return;
    }
    const covered = cells.slice(0, Math.max(1, Math.min(cells.length, length)));
    const centre = (p) => ({ x: (p.x + 0.5) * cs, y: (p.y + 0.5) * cs });
    const a = centre(covered[0]);
    const z = centre(covered[covered.length - 1]);
    const horizontal = cells.length < 2 || cells[0].y === cells[1].y;
    const band = cs * 0.5;
    // The cells it was authored across but starts retracted from: outlined, over art or placeholder alike.
    ctx.save();
    ctx.setLineDash([cs * 0.08, cs * 0.06]);
    ctx.lineWidth = Math.max(1, cs * 0.025);
    ctx.strokeStyle = "rgba(250,204,21,0.7)";
    for (const p of cells.slice(covered.length)) {
        ctx.strokeRect(p.x * cs + cs * 0.12, p.y * cs + cs * 0.12, cs * 0.76, cs * 0.76);
    }
    ctx.setLineDash([]);
    if (drawSlidingDoorSprite(ctx, cells, covered.length, cs)) {
        ctx.restore();
        return;
    }
    const x0 = Math.min(a.x, z.x) - (horizontal ? 0 : band / 2);
    const y0 = Math.min(a.y, z.y) - (horizontal ? band / 2 : 0);
    const w = horizontal ? Math.abs(z.x - a.x) : band;
    const h = horizontal ? band : Math.abs(z.y - a.y);
    ctx.fillStyle = "#EAB308";
    ctx.fillRect(x0, y0, Math.max(w, 1), Math.max(h, 1));
    ctx.strokeStyle = "rgba(0,0,0,0.55)";
    ctx.lineWidth = Math.max(1, cs * 0.02);
    const ticks = Math.max(1, Math.round((horizontal ? w : h) / (cs * 0.12)));
    for (let i = 1; i < ticks; i++) {
        ctx.beginPath();
        if (horizontal) {
            const tx = x0 + (w * i) / ticks;
            ctx.moveTo(tx, y0);
            ctx.lineTo(tx, y0 + band * (i % 4 === 0 ? 0.5 : 0.28));
        }
        else {
            const ty = y0 + (h * i) / ticks;
            ctx.moveTo(x0, ty);
            ctx.lineTo(x0 + band * (i % 4 === 0 ? 0.5 : 0.28), ty);
        }
        ctx.stroke();
    }
    roundRect(ctx, z.x - cs * 0.17, z.y - cs * 0.17, cs * 0.34, cs * 0.34, cs * 0.08);
    ctx.fillStyle = "#B45309";
    ctx.fill();
    roundRect(ctx, a.x - cs * 0.4, a.y - cs * 0.4, cs * 0.8, cs * 0.8, cs * 0.16);
    const g = ctx.createLinearGradient(0, a.y - cs * 0.4, 0, a.y + cs * 0.4);
    g.addColorStop(0, "#EF4444");
    g.addColorStop(1, "#991B1B");
    ctx.fillStyle = g;
    ctx.fill();
    ctx.strokeStyle = "rgba(0,0,0,0.4)";
    ctx.stroke();
    ctx.fillStyle = "#fff";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.font = `bold ${Math.round(cs * 0.3)}px "Segoe UI", Arial, sans-serif`;
    ctx.fillText(String(length), a.x, a.y - cs * 0.05);
    ctx.restore();
    drawTag(ctx, a.x, a.y + cs * 0.3, cs, "DOOR");
}
// A laser gate across the run from `a` to `b` (grid coords), numbered `n` so its key can say which gate it
// opens. Emitters on both ends, three beams between them, a padlock half way.
export function drawLaserGate(ctx, a, b, cs, n, color, pair = 0) {
    if (drawLaserGateSprite(ctx, a, b, cs, pair)) {
        drawTag(ctx, ((a.x + b.x) / 2 + 0.5) * cs, ((a.y + b.y) / 2 + 0.5) * cs + cs * 0.4, cs, `LASER ${n}`);
        return;
    }
    const ca = { x: (a.x + 0.5) * cs, y: (a.y + 0.5) * cs };
    const cb = { x: (b.x + 0.5) * cs, y: (b.y + 0.5) * cs };
    const horizontal = a.y === b.y;
    ctx.save();
    ctx.strokeStyle = color;
    ctx.lineWidth = Math.max(1.5, cs * 0.035);
    ctx.shadowColor = color;
    ctx.shadowBlur = cs * 0.12;
    for (const o of [-0.18, 0, 0.18]) {
        ctx.beginPath();
        if (horizontal) {
            ctx.moveTo(ca.x, ca.y + o * cs);
            ctx.lineTo(cb.x, cb.y + o * cs);
        }
        else {
            ctx.moveTo(ca.x + o * cs, ca.y);
            ctx.lineTo(cb.x + o * cs, cb.y);
        }
        ctx.stroke();
    }
    ctx.shadowBlur = 0;
    for (const c of [ca, cb]) {
        roundRect(ctx, c.x - cs * 0.13, c.y - cs * 0.3, cs * 0.26, cs * 0.6, cs * 0.08);
        ctx.fillStyle = "#374151";
        ctx.fill();
        ctx.fillStyle = color;
        for (const o of [-0.18, 0, 0.18]) {
            ctx.beginPath();
            ctx.arc(c.x, c.y + o * cs, cs * 0.045, 0, Math.PI * 2);
            ctx.fill();
        }
    }
    const mx = (ca.x + cb.x) / 2;
    const my = (ca.y + cb.y) / 2;
    drawPadlock(ctx, mx, my, cs * 0.34);
    ctx.restore();
    drawTag(ctx, mx, my + cs * 0.32, cs, `LASER ${n}`);
}
// The key box of laser gate `n` (grid coords): a key glyph in the corner and a tag, so each key can be
// matched to its gate on a board with several.
export function drawLaserKey(ctx, k, cs, n, color, pair = 0) {
    if (drawLaserKeySprite(ctx, k, cs, pair)) {
        drawTag(ctx, (k.x + 0.5) * cs, k.y * cs + cs * 0.84, cs, `KEY ${n}`);
        return;
    }
    const x = k.x * cs;
    const y = k.y * cs;
    ctx.save();
    ctx.strokeStyle = color;
    ctx.lineWidth = Math.max(2, cs * 0.05);
    ctx.lineCap = "round";
    const cx = x + cs * 0.72;
    const cy = y + cs * 0.3;
    ctx.beginPath();
    ctx.arc(cx, cy, cs * 0.09, 0, Math.PI * 2);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(cx - cs * 0.07, cy + cs * 0.07);
    ctx.lineTo(cx - cs * 0.3, cy + cs * 0.3);
    ctx.moveTo(cx - cs * 0.22, cy + cs * 0.22);
    ctx.lineTo(cx - cs * 0.15, cy + cs * 0.29);
    ctx.stroke();
    ctx.restore();
    drawTag(ctx, x + cs / 2, y + cs * 0.8, cs, `KEY ${n}`);
}
// Tool box: a wooden crate with a handle — it throws its whole load into the closest free cells, then goes.
// Pipe (TWGS.Gameplay Pipe), on the BOARD: the head on (head.x, head.y), its body straight up to the top of
// `topRow` (the topmost cell of its column the template leaves empty), its collar there. In board pixels, the
// board's top-left at (0, 0), `cs` per cell. Falls back to plain shapes while the sprites decode.
export function drawPipe(ctx, cs, head, topRow, count = 0) {
    if (drawPipeSprite(ctx, cs, head, topRow, count))
        return;
    const hex = TUNNEL_HEX;
    const cx = (head.x + 0.5) * cs;
    const bodyW = cs * 0.38;
    const headY = head.y * cs + cs * 0.42;
    ctx.fillStyle = shade(hex, -0.1);
    ctx.fillRect(cx - bodyW / 2, topRow * cs + cs * 0.12, bodyW, headY - (topRow * cs + cs * 0.12));
    roundRect(ctx, cx - bodyW * 0.6, topRow * cs + cs * 0.06, bodyW * 1.2, cs * 0.14, cs * 0.04);
    ctx.fillStyle = hex;
    ctx.fill();
    roundRect(ctx, cx - cs * 0.42, headY, cs * 0.84, cs * 0.4, cs * 0.1);
    ctx.fillStyle = hex;
    ctx.fill();
    ctx.strokeStyle = "rgba(0,0,0,0.4)";
    ctx.lineWidth = Math.max(1, cs * 0.02);
    ctx.stroke();
    if (count > 0) {
        ctx.fillStyle = "#FFF6E6";
        ctx.font = `900 ${Math.round(cs * 0.3)}px "Segoe UI", Arial, sans-serif`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(String(count), cx, headY + cs * 0.2);
    }
}
// A pipe in ONE cell (the palette tile, a lone cell): its head, with the collar right above it - what a pipe
// on the board's top row looks like in game.
export function drawPipeIcon(ctx, S, count = 0) {
    drawPipe(ctx, S, { x: 0, y: 0 }, 0, count);
}
export function drawToolBox(ctx, S, count = 0) {
    if (drawToolBoxSprite(ctx, S, count))
        return;
    const m = S * 0.1;
    roundRect(ctx, m, S * 0.3, S - 2 * m, S * 0.58, S * 0.08);
    const g = ctx.createLinearGradient(0, S * 0.3, 0, S * 0.88);
    g.addColorStop(0, "#C2410C");
    g.addColorStop(1, "#7C2D12");
    ctx.fillStyle = g;
    ctx.fill();
    ctx.strokeStyle = "rgba(0,0,0,0.45)";
    ctx.lineWidth = Math.max(1, S * 0.02);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(m, S * 0.5);
    ctx.lineTo(S - m, S * 0.5);
    ctx.stroke();
    ctx.lineWidth = Math.max(2, S * 0.05);
    ctx.strokeStyle = "#9CA3AF";
    ctx.beginPath();
    ctx.moveTo(S * 0.36, S * 0.3);
    ctx.lineTo(S * 0.36, S * 0.2);
    ctx.lineTo(S * 0.64, S * 0.2);
    ctx.lineTo(S * 0.64, S * 0.3);
    ctx.stroke();
    drawTag(ctx, S / 2, S * 0.7, S, "TOOLBOX");
}
// Propeller (the game's Propeller, placeholder art there too): a steel housing with two red blades on a hub,
// and a notch on the side it is tied to — `direction` null for the palette tile, which is tied to nothing.
// Drawn in code, after the game's dummy sprites; swap for sprites when the real art lands.
export function drawPropeller(ctx, S, direction = null) {
    const m = S * 0.08;
    roundRect(ctx, m, m + S * 0.05, S - 2 * m, S - 2 * m - S * 0.02, S * 0.16);
    ctx.fillStyle = "#34405A";
    ctx.fill();
    roundRect(ctx, m, m, S - 2 * m, S - 2 * m - S * 0.05, S * 0.16);
    ctx.fillStyle = "#6074A0";
    ctx.fill();
    roundRect(ctx, m + S * 0.08, m + S * 0.06, S - 2 * m - S * 0.16, S * 0.32, S * 0.1);
    ctx.fillStyle = "#8298BA";
    ctx.fill();
    for (const [rx, ry] of [[0.2, 0.2], [0.8, 0.2], [0.2, 0.75], [0.8, 0.75]]) {
        ctx.beginPath();
        ctx.arc(S * rx, S * ry, S * 0.035, 0, Math.PI * 2);
        ctx.fillStyle = "#46546E";
        ctx.fill();
    }
    if (direction !== null) {
        // The tied side: a small chevron on the housing's edge, pointing at the box.
        const cx = S / 2, cy = S * 0.47;
        const v = direction === GridDirection.Top ? [0, -1] : direction === GridDirection.Bottom ? [0, 1] : direction === GridDirection.Left ? [-1, 0] : [1, 0];
        const tip = { x: cx + v[0] * S * 0.4, y: cy + v[1] * S * 0.4 };
        const back = { x: cx + v[0] * S * 0.28, y: cy + v[1] * S * 0.28 };
        const side = { x: -v[1] * S * 0.08, y: v[0] * S * 0.08 };
        ctx.beginPath();
        ctx.moveTo(tip.x, tip.y);
        ctx.lineTo(back.x + side.x, back.y + side.y);
        ctx.lineTo(back.x - side.x, back.y - side.y);
        ctx.closePath();
        ctx.fillStyle = "#FDE68A";
        ctx.fill();
    }
    // Two blades across the hub, at a slant so they read as a propeller rather than a bar.
    const hx = S / 2, hy = S * 0.47;
    ctx.save();
    ctx.translate(hx, hy);
    ctx.rotate(-0.35);
    for (const sgn of [-1, 1]) {
        ellipse(ctx, sgn * S * 0.21, 0, S * 0.2, S * 0.07);
        ctx.fillStyle = "#E2523A";
        ctx.fill();
        ctx.beginPath();
        ctx.arc(sgn * S * 0.39, 0, S * 0.045, 0, Math.PI * 2);
        ctx.fillStyle = "#FAD650";
        ctx.fill();
    }
    ctx.restore();
    ctx.beginPath();
    ctx.arc(hx, hy, S * 0.09, 0, Math.PI * 2);
    ctx.fillStyle = "#3C4250";
    ctx.fill();
    ctx.beginPath();
    ctx.arc(hx, hy, S * 0.05, 0, Math.PI * 2);
    ctx.fillStyle = "#C8CEDC";
    ctx.fill();
}
// The box hidden under a propeller, as a chip in the cell's bottom-right corner: a little closed box in its
// colour (grey while authoring, the dealt one once compiled), so the board says what appears when it flies.
export function drawHiddenBoxChip(ctx, S, hex) {
    const w = S * 0.3;
    const x = S - w - S * 0.05, y = S - w - S * 0.05;
    roundRect(ctx, x, y + w * 0.08, w, w, w * 0.22);
    ctx.fillStyle = shade(hex, -0.3);
    ctx.fill();
    roundRect(ctx, x, y, w, w, w * 0.22);
    ctx.fillStyle = hex;
    ctx.fill();
    ctx.strokeStyle = "rgba(255,255,255,0.9)";
    ctx.lineWidth = Math.max(1, S * 0.02);
    ctx.stroke();
    roundRect(ctx, x + w * 0.18, y + w * 0.16, w * 0.64, w * 0.2, w * 0.08);
    ctx.fillStyle = shade(hex, 0.25);
    ctx.fill();
}
// The clamp tying a propeller to its box, on the board (board pixels): a short steel bar across the border
// between the two cell centres, over both — as the game lays its connector.
export function drawPropellerTie(ctx, x1, y1, x2, y2, cs) {
    const mx = (x1 + x2) / 2, my = (y1 + y2) / 2;
    const vertical = Math.abs(y2 - y1) > Math.abs(x2 - x1);
    const len = cs * 0.46, th = cs * 0.17;
    ctx.save();
    ctx.translate(mx, my);
    if (vertical)
        ctx.rotate(Math.PI / 2);
    roundRect(ctx, -len / 2, -th / 2 + th * 0.12, len, th, th / 2);
    ctx.fillStyle = "#28303C";
    ctx.fill();
    roundRect(ctx, -len / 2, -th / 2, len, th, th / 2);
    const g = ctx.createLinearGradient(0, -th / 2, 0, th / 2);
    g.addColorStop(0, shade(METAL_HEX, 0.14));
    g.addColorStop(1, shade(METAL_HEX, -0.16));
    ctx.fillStyle = g;
    ctx.fill();
    for (const sx of [-len * 0.3, len * 0.3]) {
        ctx.beginPath();
        ctx.arc(sx, 0, th * 0.22, 0, Math.PI * 2);
        ctx.fillStyle = "#46546E";
        ctx.fill();
    }
    ctx.restore();
}
// A glass panel (absolute coords, the rect it covers): see-through, with a disc and a count per colour it wants.
export function drawGlassPanel(ctx, x, y, w, h, goals) {
    const min = Math.min(w, h);
    const inset = min * 0.03;
    ctx.save();
    roundRect(ctx, x + inset, y + inset, w - 2 * inset, h - 2 * inset, min * 0.1);
    ctx.fillStyle = "rgba(190,235,255,0.25)";
    ctx.fill();
    ctx.lineWidth = Math.max(1.5, min * 0.025);
    ctx.strokeStyle = "rgba(235,250,255,0.85)";
    ctx.stroke();
    const r = min * 0.13;
    ctx.font = `bold ${Math.round(r * 1.3)}px "Segoe UI", Arial, sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.lineJoin = "round";
    goals.forEach((goal, i) => {
        const cx = x + w / 2 + (i % 2 - 0.5) * min * 0.32;
        const cy = y + h / 2 + (Math.floor(i / 2) - 0.5) * min * 0.32;
        ctx.beginPath();
        ctx.arc(cx, cy, r, 0, Math.PI * 2);
        ctx.fillStyle = MARBLE_COLOR_HEX[goal.color] ?? NEUTRAL_BOX_HEX;
        ctx.fill();
        ctx.lineWidth = Math.max(1, min * 0.015);
        ctx.strokeStyle = "rgba(255,255,255,0.9)";
        ctx.stroke();
        ctx.lineWidth = Math.max(2, min * 0.03);
        ctx.strokeStyle = "rgba(15,23,42,0.85)";
        ctx.strokeText(String(goal.count), cx, cy);
        ctx.fillStyle = "#fff";
        ctx.fillText(String(goal.count), cx, cy);
    });
    ctx.restore();
}
// A metal box (absolute coords, the rect it covers): the lock panel's plate crossed by two chains, with its
// hits left written on it. Three hits = both chains on; two = one chain; one = no chains, just the plate.
export function drawMetalBox(ctx, x, y, w, h, hits, cs) {
    if (cs !== undefined && drawMetalBoxSprite(ctx, x, y, w, h, cs, hits))
        return;
    ctx.save();
    ctx.globalAlpha *= 0.92; // multiplied, so a caller fading the cover still fades it
    drawMetalRect(ctx, x, y, w, h, 0);
    ctx.restore();
    const chains = Math.max(0, Math.min(2, hits - 1));
    ctx.save();
    ctx.strokeStyle = "#4B5563";
    ctx.lineWidth = Math.max(2, Math.min(w, h) * 0.05);
    ctx.setLineDash([Math.min(w, h) * 0.07, Math.min(w, h) * 0.04]);
    if (chains >= 1) {
        ctx.beginPath();
        ctx.moveTo(x + w * 0.1, y + h * 0.1);
        ctx.lineTo(x + w * 0.9, y + h * 0.9);
        ctx.stroke();
    }
    if (chains >= 2) {
        ctx.beginPath();
        ctx.moveTo(x + w * 0.9, y + h * 0.1);
        ctx.lineTo(x + w * 0.1, y + h * 0.9);
        ctx.stroke();
    }
    ctx.restore();
    const cell = Math.min(w, h) / 2;
    drawTag(ctx, x + w / 2, y + h / 2, cell, `METAL ${hits}`);
}
export function drawElevator(ctx, x, y, w, h, cs) {
    if (cs !== undefined && drawElevatorSprite(ctx, x, y, w, h, cs))
        return;
    drawMetalRect(ctx, x, y, w, h, 0);
    drawTag(ctx, x + w / 2, y + h / 2, Math.min(w, h) / 2, "ELEVATOR");
}
// A bolt through the run from its HEAD `head` to `far` (grid coords): a shaft along the run, a hex head on the
// head cell, and a dashed outline + tag on the one cell whose box unlocks it.
export function drawBolt(ctx, head, far, cs) {
    const ch = { x: (head.x + 0.5) * cs, y: (head.y + 0.5) * cs };
    const cf = { x: (far.x + 0.5) * cs, y: (far.y + 0.5) * cs };
    if (drawBoltSprite(ctx, head, far, cs)) {
        drawBoltUnlock(ctx, head, far, cs);
        return;
    }
    ctx.save();
    ctx.lineCap = "round";
    ctx.strokeStyle = "#6B7280";
    ctx.lineWidth = cs * 0.16;
    ctx.beginPath();
    ctx.moveTo(ch.x, ch.y);
    ctx.lineTo(cf.x, cf.y);
    ctx.stroke();
    ctx.strokeStyle = "#D1D5DB";
    ctx.lineWidth = cs * 0.05;
    ctx.stroke();
    ctx.beginPath();
    for (let i = 0; i < 6; i++) {
        const a = (Math.PI / 3) * i;
        const px = ch.x + Math.cos(a) * cs * 0.22;
        const py = ch.y + Math.sin(a) * cs * 0.22;
        if (i === 0)
            ctx.moveTo(px, py);
        else
            ctx.lineTo(px, py);
    }
    ctx.closePath();
    ctx.fillStyle = "#9CA3AF";
    ctx.fill();
    ctx.strokeStyle = "rgba(0,0,0,0.5)";
    ctx.lineWidth = Math.max(1, cs * 0.02);
    ctx.stroke();
    const dx = Math.sign(head.x - far.x);
    const dy = Math.sign(head.y - far.y);
    if ((dx !== 0) !== (dy !== 0)) {
        const u = { x: head.x + dx, y: head.y + dy };
        ctx.setLineDash([cs * 0.1, cs * 0.07]);
        ctx.strokeStyle = "#A3E635";
        ctx.lineWidth = Math.max(2, cs * 0.045);
        ctx.strokeRect(u.x * cs + cs * 0.08, u.y * cs + cs * 0.08, cs * 0.84, cs * 0.84);
        ctx.restore();
        drawTag(ctx, (u.x + 0.5) * cs, (u.y + 0.25) * cs, cs, "UNLOCK");
    }
    else {
        ctx.restore();
    }
    drawTag(ctx, ch.x, ch.y + cs * 0.3, cs, "BOLT");
}
// The one cell whose box unlocks a bolt (one step beyond the head, in line): no game art marks it, so the
// editor outlines it and says so.
function drawBoltUnlock(ctx, head, far, cs) {
    const dx = Math.sign(head.x - far.x);
    const dy = Math.sign(head.y - far.y);
    if ((dx !== 0) === (dy !== 0))
        return;
    const u = { x: head.x + dx, y: head.y + dy };
    ctx.save();
    ctx.setLineDash([cs * 0.1, cs * 0.07]);
    ctx.strokeStyle = "#A3E635";
    ctx.lineWidth = Math.max(2, cs * 0.045);
    ctx.strokeRect(u.x * cs + cs * 0.08, u.y * cs + cs * 0.08, cs * 0.84, cs * 0.84);
    ctx.restore();
    drawTag(ctx, (u.x + 0.5) * cs, (u.y + 0.25) * cs, cs, "UNLOCK");
}
function drawPadlock(ctx, cx, cy, size) {
    ctx.save();
    ctx.lineWidth = Math.max(1.5, size * 0.12);
    ctx.strokeStyle = "#9CA3AF";
    ctx.beginPath();
    ctx.arc(cx, cy - size * 0.1, size * 0.25, Math.PI, 0);
    ctx.stroke();
    roundRect(ctx, cx - size * 0.38, cy - size * 0.1, size * 0.76, size * 0.55, size * 0.1);
    ctx.fillStyle = "#FACC15";
    ctx.fill();
    ctx.strokeStyle = "rgba(0,0,0,0.45)";
    ctx.lineWidth = Math.max(1, size * 0.05);
    ctx.stroke();
    ctx.restore();
}
